import React, { FC, useCallback, useEffect, useState } from 'react';
import funLogoImg from 'assets/images/fun_studio_logo.png';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import {
  I18N_LOCAL_STORAGE_KEY,
  I18nNamespace,
  Languages,
} from 'constants/i18n.const';
import { AppRouteConst, AuthRouteConst } from 'constants/route.const';
import { logoutAction } from 'store/features/auth/auth.action';
import { useTranslation } from 'hooks/useTranslation';
import Menu from 'components/menu/Menu';
import MenuItem from 'components/menu/MenuItem';
import Dropdown from 'components/dropdown/Dropdown';
import DropdownMenu from 'components/dropdown/DropdownMenu';
import DropdownItem from 'components/dropdown/DropdownItem';
import MenuMenu from 'components/menu/MenuMenu';
import Image from 'components/image/Image';
import i18n from 'i18n';
import { AssetIcon } from 'assets/icon/Icons';
import { useOptions } from 'hooks/useOptions';
import { find, isEmpty, map } from 'lodash';
import { setDashboardStateAction } from 'store/features/dashboard/dashboard.slice';
import { setLS } from 'helpers/common.helper';
import HeaderBreadcrumb from './breadcrumb/HeaderBreadcrumb';
import './app-header.css';

interface AppHeaderProps {}

const AppHeader: FC<AppHeaderProps> = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { T } = useTranslation(I18nNamespace.COMMON);
  const { userIsLogging } = useAppSelector((s) => s.user);
  const { listCurrency } = useAppSelector((s) => s.prefetch);
  const { dashboardCurrencyid } = useAppSelector((s) => s.dashboard);
  const { languageOptions } = useOptions();
  const [dashboardCurrency, setDashboardCurrency] = useState<any>();

  const handleLogout = useCallback(() => {
    dispatch(logoutAction());
    navigate(AuthRouteConst.LOGIN);
  }, [dispatch, navigate]);

  const handleChangeLanguage = useCallback((language: Languages) => {
    i18n.changeLanguage(language);
    setLS(I18N_LOCAL_STORAGE_KEY, language);
  }, []);

  const handleChangeCurrency = useCallback(
    (id: number) => {
      const currency = find(listCurrency, (i) => i?.id === id);
      setDashboardCurrency(currency?.symbol ?? '');
      dispatch(
        setDashboardStateAction({
          dashboardCurrencyid: currency?.id,
        }),
      );
    },
    [dispatch, listCurrency],
  );

  const getCurrentLanguageDisplay = () => {
    switch (i18n.language) {
      case Languages.VIETNAM:
        return (
          <>
            <AssetIcon.VietnamIcon style={{ margin: '0 5px' }} />
            {''} {T('language.vietnamese')}
          </>
        );
      case Languages.ENGLISH:
        return (
          <>
            <AssetIcon.EnglishIcon style={{ margin: '0 5px' }} />
            {''} {T('language.english')}
          </>
        );
      default:
        return T('language.english');
    }
  };

  useEffect(() => {
    const currency = find(listCurrency, (i) => i?.id === dashboardCurrencyid);
    if (isEmpty(dashboardCurrency)) {
      setDashboardCurrency(currency?.symbol);
    }
  }, [dashboardCurrency, dashboardCurrencyid, dispatch, listCurrency]);

  return (
    <header>
      <Menu attached="top">
        <MenuItem as="a" header className="logo-wrapper">
          <NavLink to={AppRouteConst.HOME} className="logo-link">
            <Image size="mini" src={funLogoImg} />
          </NavLink>
        </MenuItem>
        <MenuItem header>
          <HeaderBreadcrumb />
        </MenuItem>

        <MenuMenu position="right">
          <Dropdown item text={dashboardCurrency}>
            <DropdownMenu>
              {map(listCurrency, (currency) => (
                <DropdownItem
                  key={currency?.id}
                  onClick={() => handleChangeCurrency(currency?.id)}
                >
                  <div className="flex items-center justify-start gap-2">
                    <div className="flex justify-center items-center max-w-[40px]">
                      {currency?.symbol}
                    </div>
                    <div className="flex justify-center items-center">
                      {currency?.name}
                    </div>
                  </div>
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
          <Dropdown item trigger={getCurrentLanguageDisplay()}>
            <DropdownMenu>
              {map(languageOptions, (lang) => (
                <DropdownItem
                  key={lang.key}
                  onClick={() => handleChangeLanguage(lang.key)}
                >
                  <div className="flex items-center justify-start gap-2">
                    {lang.icon}
                    {lang.label}
                  </div>
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
          <Dropdown item simple text={userIsLogging?.displayName}>
            <DropdownMenu>
              <DropdownItem as={Link} to={AppRouteConst.PROFILE.MY_PROFILE}>
                {T('layout.profile')}
              </DropdownItem>
              <DropdownItem
                as={Link}
                to={AppRouteConst.PROFILE.CHANGE_PASSWORD}
              >
                {T('layout.changePassword')}
              </DropdownItem>
              <DropdownItem as={Link} to={AppRouteConst.PROFILE.CHANGE_PINCODE}>
                {T('layout.changePinCode')}
              </DropdownItem>
              <DropdownItem onClick={handleLogout}>
                {T('layout.logout')}
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </MenuMenu>
      </Menu>
    </header>
  );
};

export default AppHeader;
